import { useCallback, useEffect, useMemo, useState } from 'react'
import { CopyIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { trackEvent } from '@/utils/analytics'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { shareUrl } from '@/utils/shareLinks'
import type { Share, ShareRecipientResult, ShareTarget } from '@/types/shares'
import Modal from './Modal'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { IntuiAvatar } from '@/components/intueri/IntuiAvatar'
import { IntuiBadge } from '@/components/intueri/IntuiBadge'
import {
  ShareRecipientPicker,
  type SelectedRecipient,
} from '@/components/Share/ShareRecipientPicker'

type ShareModalProps = {
  isOpen: boolean
  setShow: (isOpen: boolean) => void
  /**
   * What is being shared. Optional so the board canvas can render the modal
   * before a board has loaded; the dialog renders nothing useful without it.
   */
  target?: ShareTarget
}

/**
 * FS-663 share dialog.
 *
 * Replaces a stub that collected emails into `useState`, fired a PostHog event
 * on Confirm and did nothing else — no request, no grant, no email.
 *
 * The audience contract is stated in the dialog on purpose: this is company-only
 * sharing. The link is an ordinary app URL, not a capability, so it is useless
 * to anyone outside the organisation and the note must not imply otherwise.
 */
const ShareModal = ({ isOpen, setShow, target }: ShareModalProps) => {
  const { t } = useTranslation()

  const [recipients, setRecipients] = useState<SelectedRecipient[]>([])
  const [message, setMessage] = useState('')
  const [copied, setCopied] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const sharesByResource = useAppStore((s) => s.sharesByResource)
  const isSharesLoading = useAppStore((s) => s.isSharesLoading)
  const fetchShares = useAppStore((s) => s.fetchShares)
  const createShares = useAppStore((s) => s.createShares)
  const revokeShare = useAppStore((s) => s.revokeShare)
  const organizationName = useAppStore((s) => s.organizationName)
  const currentUserEmail = useAppStore((s) => s.user?.email)
  const currentUserId = useAppStore((s) => s.user?.userId)

  // Depend on the PRIMITIVE fields, never on `target` itself.
  //
  // Every call site builds `target` as an inline object literal, so its identity
  // changes on every render of the parent. Depending on the object made this
  // effect re-run each render, and `fetchShares` sets store state → re-render →
  // re-fetch: an infinite loop that hammered GET /v1/shares (observed: 104 calls
  // for one board) and took the whole page down with it.
  const targetType = target?.type
  const targetId = target?.id

  const grants: Share[] = useMemo(
    () => (targetType && targetId ? (sharesByResource[`${targetType}:${targetId}`] ?? []) : []),
    [sharesByResource, targetType, targetId]
  )

  const tenantId = useAppStore((s) => s.tenantId)
  const url = target ? shareUrl(target, undefined, tenantId) : ''

  useEffect(() => {
    if (isOpen && targetType && targetId) {
      void fetchShares(targetType, targetId)
    }
  }, [isOpen, targetType, targetId, fetchShares])

  const handleClose = useCallback(() => {
    setRecipients([])
    setMessage('')
    setCopied(false)
    setShow(false)
  }, [setShow])

  const handleCopyLink = useCallback(async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      if (target?.type === 'workspace') {
        trackEvent('workspace-shared', { workspace_id: target.id, share_method: 'link' })
      }
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }, [url, target])

  /**
   * Report the per-recipient outcome rather than a single success toast: one
   * request can simultaneously share, invite, be refused by policy, and fail to
   * deliver mail. Collapsing that would tell the user someone was notified when
   * they were not.
   */
  const reportResults = useCallback(
    (results: ShareRecipientResult[]) => {
      const notified = results.filter((r) => r.status === 'shared' || r.status === 'resent')
      const invited = results.filter((r) => r.status === 'invited')
      const needsAdmin = results.filter((r) => r.status === 'invite_required')
      const emailFailed = results.filter((r) => r.status === 'email_failed')

      if (notified.length + invited.length > 0) {
        toast.success(
          t('common:share_modal.sent', { count: notified.length + invited.length })
        )
      }
      needsAdmin.forEach((r) =>
        toast.warning(t('common:share_modal.invite_required_note', { email: r.email }))
      )
      emailFailed.forEach((r) =>
        toast.warning(t('common:share_modal.email_failed_note', { email: r.email }))
      )
    },
    [t]
  )

  const handleSend = useCallback(async () => {
    if (!target || recipients.length === 0) return

    setIsSending(true)
    try {
      const response = await createShares({
        resource_type: target.type,
        resource_id: target.id,
        emails: recipients.map((r) => r.email),
        ...(message.trim() ? { message: message.trim() } : {}),
      })

      if (!response) return // the slice already surfaced the error

      reportResults(response.results)
      setRecipients([])
      setMessage('')

      if (target.type === 'workspace') {
        trackEvent('workspace-shared', { workspace_id: target.id, share_method: 'email' })
      }
    } finally {
      setIsSending(false)
    }
  }, [target, recipients, message, createShares, reportResults])

  const title =
    target?.type === 'conversation'
      ? t('common:share_modal.title_conversation')
      : t('common:share_modal.title_board')

  const description =
    target?.type === 'conversation'
      ? t('common:share_modal.description_conversation')
      : t('common:share_modal.description')

  const linkLabel =
    target?.type === 'conversation'
      ? t('common:share_modal.chat_link')
      : t('common:share_modal.share_link')

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="w-[560px] max-w-[95vw]"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSending}>
            {t('common:share_modal.close')}
          </Button>
          <Button onClick={handleSend} disabled={recipients.length === 0 || isSending}>
            {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSending ? t('common:share_modal.sending') : t('common:share_modal.send')}
          </Button>
        </>
      }
    >
      <div className="grid gap-6 py-2">
        <p className="text-gray-500 dark:text-dark-text-muted">{description}</p>

        <div className="flex flex-col gap-3">
          <label className="text-sm font-medium">
            {t('common:share_modal.email_label')}
          </label>
          <ShareRecipientPicker
            selected={recipients}
            onChange={setRecipients}
            excludeEmails={grants.map((g) => g.recipient_email)}
            {...(organizationName ? { organizationName } : {})}
            disabled={isSending}
          />
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSending}
            rows={2}
            maxLength={500}
            placeholder={t('common:share_modal.description')}
            className="resize-none"
          />
        </div>

        <div className="flex flex-col gap-3">
          <h4 className="text-sm font-medium">{t('common:share_modal.who_has_access')}</h4>

          {isSharesLoading && grants.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              {t('common:share_modal.loading_access')}
            </p>
          ) : grants.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              {t('common:share_modal.no_members')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {grants.map((grant) => (
                <li key={grant.id} className="flex items-center gap-3">
                  <IntuiAvatar
                    size="sm"
                    fallback={grant.recipient_email.slice(0, 2).toUpperCase()}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {grant.recipient_email}
                    {grant.recipient_email === currentUserEmail && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({t('common:share_modal.badge_you')})
                      </span>
                    )}
                  </span>
                  <IntuiBadge variant={grant.pending_invite ? 'warning' : 'secondary'}>
                    {grant.pending_invite
                      ? t('common:share_modal.badge_pending')
                      : t('common:share_modal.badge_can_view')}
                  </IntuiBadge>
                  {/* Revoke is owner-only server-side, so a non-owner clicking
                      this would only ever get a 403 and a generic error toast
                      (greptile on #27484). Offer it to the sharer alone. */}
                  {grant.shared_by_user_id === currentUserId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (!target) return
                        void revokeShare(grant.id, target.type, target.id)
                      }}
                    >
                      {t('common:share_modal.revoke')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <h4 className="text-sm font-medium">{linkLabel}</h4>
          <div className="flex items-center gap-3">
            <Input className="w-full" value={url} type="text" readOnly />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CopyIcon
                    className="w-8 cursor-pointer text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    onClick={handleCopyLink}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {copied
                      ? t('common:share_modal.copied')
                      : t('common:share_modal.copy_link')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-xs text-gray-500 dark:text-dark-text-muted">
            {t('common:share_modal.company_only_note', {
              organization: organizationName || 'your organization',
            })}
          </p>
        </div>
      </div>
    </Modal>
  )
}

export default ShareModal
